import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { useEffect, useRef, type PropsWithChildren, type ReactNode } from "react";
import type { DocumentStatus } from "../types";

export function PageHeader({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="module-header">
      <div title={description}>
        <h2>{title}</h2>
        <span className="module-subtitle">{eyebrow}</span>
      </div>
      {action}
    </div>
  );
}

export function Tabs({
  tabs,
  value,
  onChange
}: {
  tabs: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="module-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          className={value === tab.value ? "active" : ""}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: DocumentStatus }) {
  const label = status === "Draft"
    ? "Nháp"
    : status === "InProgress"
      ? "Đang thực hiện"
      : status === "Completed"
        ? "Hoàn tất"
        : "Đã hủy";
  return <span className={`status-pill status-${status.toLowerCase()}`}>{label}</span>;
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  wide = false,
  error
}: PropsWithChildren<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  wide?: boolean;
  error?: string;
}>) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!error || !bodyRef.current) return;
    bodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
    const invalid = bodyRef.current.querySelector<HTMLElement>("[aria-invalid='true'], .invalid-value, input:invalid, select:invalid, textarea:invalid");
    invalid?.focus();
  }, [error]);
  return (
    <div className="modal-backdrop" role="presentation">
      <section className={`modal-card ${wide ? "modal-wide" : ""}`} role="dialog" aria-modal="true">
        <header className="modal-header">
          <div>
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Đóng">
            <X size={19} />
          </button>
        </header>
        <div className="modal-body" ref={bodyRef}>
          {error && <Message tone="error">{error}</Message>}
          {children}
        </div>
      </section>
    </div>
  );
}

export function Message({
  tone,
  children
}: PropsWithChildren<{ tone: "success" | "error" }>) {
  return (
    <div className={`form-message ${tone}`}>
      {tone === "success" ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
      <span>{children}</span>
    </div>
  );
}

export const formatMoney = (value: number | null | undefined) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 }).format(value ?? 0);

export const formatNumber = (value: number | null | undefined, digits = 3) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: digits }).format(value ?? 0);

export const formatDate = (value?: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString("vi-VN") : "—";

export const today = () => {
  const date = new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};
